import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";

import { Badge } from "../../ui/badge";
import {
  Plus,
  Edit3,
  Trash2,
  Folder,
  Globe,
  Search,
  ChevronLeft,
  ChevronRight,
  FileTerminal,
  X,
  Download,
  Upload,
  List,
  Clock,
  Layers,
} from "lucide-react";
import { useToast } from "../../../hooks/use-toast";
import { myLocalStorage } from "../../StorageHelper";
import {
  fetchCategories,
  getCategoryItems,
  getProfiles,
} from "../PolicyProfiles/Api/policyprofileapi";
import { AppResource, PolicyProfiles } from "../PolicyProfiles/interface";
import {
  getCategoryItemsWithoutProfileId,
  postCategory,
  removeCategory,
  updateCategory,
  getResourcesByPattern,
} from "./Api/resourcecategoryapi";
import { Skeleton } from "../../ui/skeleton";
import { Device } from "../interfaces/EndpointInterfaces";
import useUserStore from "../../../services/userStore";
import ConfirmationDialog from "../Endpoints/ConfirmationDialog";
import { formatDate } from "../formatTimeAgo";
import { FaApple, FaLinux, FaWindows } from "react-icons/fa6";
import "./ResourceCategories.css";
import "../ManagementDialog.css";

interface DevicePattern {
  id: string;
  pattern: string;
  includedDevices: Device[];
  excludedDevices: Device[];
}

export default function ResourceCategoryDetails() {
  const [activeTab, setActiveTab] = useState<"windows" | "linux" | "mac">(
    "windows",
  );
  const [selectedResourceType, setSelectedResourceType] = useState<
    "app" | "folder" | "url"
  >("app");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [categoriesSearch, setCategoriesSearch] = useState("");
  const [itemsSearch, setItemsSearch] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [currentCategoriesPage, setCurrentCategoriesPage] = useState(1);
  const [currentItemsPage, setCurrentItemsPage] = useState(1);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { toast } = useToast();
  const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";
  const { email } = useUserStore((state) => state.user);

  const [patterns, setPatterns] = useState<any[]>([]);
  const [currentPattern, setCurrentPattern] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [categoryList, setCategoryList] = useState<AppResource[] | []>([]);
  const [allSelectedResources, setAllSelectedResources] = useState<any[]>([]);
  const [, setRemovedAppDupicates] = useState<any[]>([]);
  const [livePattern, setLivePattern] = useState<any[]>([]);
  const [livePatternLoading, setLivePatternLoading] = useState<boolean>(false);
  const [selectedPattern, setSelectedPattern] = useState<string>("");
  const [priority, setPriority] = useState<number>(10);
  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    useState<boolean>(false);
  const [, setProfiles] = useState<PolicyProfiles[]>([]);
  const isSelectionRef = useRef<boolean>(false);
  const DESCRIPTION_MAX_LENGTH = 25;
  const getDefaultFormData = () => ({
    name: "",
    reputationScore: 10,
    profiles: [],
    devicePatterns: [] as DevicePattern[],
    searchPatterns: "",
    tagInput: "",
    isEdit: false,
  });

  const [formData, setFormData] = useState(getDefaultFormData());

  const CATEGORIES_PER_PAGE = 6;
  const ITEMS_PER_PAGE = 8;

  const importData = () => {
    inputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    event.target.value = "";

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const jsonData = JSON.parse(e.target?.result as string);

        const importedCategories = Array.isArray(jsonData)
          ? jsonData
          : Array.isArray(jsonData?.categories)
            ? jsonData.categories
            : [];

        if (!importedCategories.length) {
          toast({
            title: "Import failed",
            description: "No valid categories found in the JSON file.",
            variant: "destructive",
          });
          return;
        }

        const normalizeSearchPatterns = (
          patterns: any,
        ): Array<{ id: string; text: string }> => {
          if (!Array.isArray(patterns)) return [];

          return patterns
            .map((pattern: any) => {
              if (typeof pattern === "string") {
                const text = pattern.trim();
                if (!text) return null;
                return {
                  id: `${Date.now()}-${Math.random()}`,
                  text,
                };
              }

              const text = String(
                pattern?.text ?? pattern?.pattern ?? "",
              ).trim();
              if (!text) return null;

              return {
                id: String(pattern?.id ?? `${Date.now()}-${Math.random()}`),
                text,
              };
            })
            .filter(Boolean) as Array<{ id: string; text: string }>;
        };

        let importedCount = 0;
        let failedCount = 0;

        for (const category of importedCategories) {
          const name = String(category?.name ?? "").trim();
          const searchPatterns = normalizeSearchPatterns(
            category?.searchPatterns,
          );

          if (!name || !searchPatterns.length) {
            failedCount += 1;
            continue;
          }
          let ostype = String(
            category?.operationSystem ?? activeTab,
          ).toUpperCase();
          const payload = {
            name,
            reputationScore: Number(category?.reputationScore ?? 10) || 10,
            searchPatterns,
            os: ostype === "MAC" || ostype === "MACOS" ? "MACOS" : ostype,
            resourceType: String(
              category?.resourceType ?? selectedResourceType,
            ).toUpperCase(),
            profiles: Array.isArray(category?.profiles)
              ? category.profiles
              : [],
            userEmail: email,
            manualExclusions: Array.isArray(category?.manualExclusions)
              ? category.manualExclusions
              : [],
            tenantName: category?.tenantName || tenantName,
          };

          try {
            await postCategory(payload);
            importedCount += 1;
          } catch (importError) {
            failedCount += 1;
            console.error(`Failed to import category: ${name}`, importError);
          }
        }

        await fetchCategoriesData(false);

        if (importedCount > 0 && failedCount === 0) {
          toast({
            title: "Import successful",
            description: `${importedCount} categories imported successfully.`,
          });
        } else if (importedCount > 0) {
          toast({
            title: "Import completed with warnings",
            description: `${importedCount} categories imported, ${failedCount} failed.`,
          });
        } else {
          toast({
            title: "Import failed",
            description: "No categories were imported.",
            variant: "destructive",
          });
        }
      } catch (err) {
        toast({
          title: "Import failed",
          description: "Invalid JSON file format.",
          variant: "destructive",
        });
      }
    };
    reader.readAsText(file);
  };

  const exportData = () => {
    if (!categoryList?.length) {
      toast({
        title: "⚠️ No data to export",
        description: "Your category list is empty.",
        variant: "destructive",
      });
      return;
    }

    const stripTenantName = (value: any): any => {
      if (Array.isArray(value)) {
        return value.map(stripTenantName);
      }

      if (value && typeof value === "object") {
        return Object.entries(value).reduce(
          (acc, [key, nestedValue]) => {
            if (key === "tenantName") {
              return acc;
            }

            acc[key] = stripTenantName(nestedValue);
            return acc;
          },
          {} as Record<string, any>,
        );
      }

      return value;
    };

    const exportPayload = stripTenantName(categoryList);

    const jsonBlob = new Blob([JSON.stringify(exportPayload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(jsonBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `categoryList.json`;
    a.click();
    URL.revokeObjectURL(url);

    toast({
      title: "Export successful",
      description: "File downloaded successfully.",
    });
  };

  const resetStateOnTabOrTypeChange = () => {
    setSelectedCategory("");
    setCategoriesSearch("");
    setItemsSearch("");
    setCurrentCategoriesPage(1);
    setCurrentItemsPage(1);
    setPatterns([]);
    setCurrentPattern("");
    setCategoryName("");
    setAllSelectedResources([]);
    setFormData(getDefaultFormData());
  };

  useEffect(() => {
    fetchProfiles(tenantName);
  }, [tenantName]);

  useEffect(() => {
    resetStateOnTabOrTypeChange();
    fetchCategoriesData();
  }, [activeTab, selectedResourceType]);

  const fetchProfiles = async (tenantName: string) => {
    let response: PolicyProfiles[] = await getProfiles(tenantName);
    if (response) {
      setProfiles(response);
    }
  };

  const handleResetCategory = () => {
    setCategoryName("");
    setPriority(10);
    setFormData(getDefaultFormData());
  };

  const fetchCategoriesData = async (isFirst = true) => {
    setCategoriesLoading(isFirst);
    const typeMapping: Record<string, string> = {
      Apps: "app",
      Folders: "folder",
      Group: "group",
      Publisher: "publisher",
      URLs: "url",
    };
    const resourceKey =
      typeMapping[
        selectedResourceType === "app"
          ? "Apps"
          : selectedResourceType === "folder"
            ? "Folders"
            : "URLs"
      ];
    try {
      const categories: AppResource[] = await fetchCategories(
        activeTab,
        tenantName,
        resourceKey,
      );
      const osFilter = activeTab === "mac" ? "MACOS" : activeTab.toUpperCase();
      const filteredCategories = categories?.filter(
        (c) => c.operationSystem?.toUpperCase() === osFilter,
      );
      setCategoryList(filteredCategories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategoryList([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const filteredCategories = useMemo(() => {
    return categoryList.filter((c) =>
      c.name.toLowerCase().includes(categoriesSearch.toLowerCase()),
    );
  }, [categoryList, categoriesSearch]);

  const totalCategoriesPages = Math.max(
    1,
    Math.ceil(filteredCategories.length / CATEGORIES_PER_PAGE),
  );
  const paginatedCategories = useMemo(() => {
    const startIndex = (currentCategoriesPage - 1) * CATEGORIES_PER_PAGE;
    if (currentCategoriesPage > totalCategoriesPages)
      setCurrentCategoriesPage(totalCategoriesPages);
    return filteredCategories.slice(
      startIndex,
      startIndex + CATEGORIES_PER_PAGE,
    );
  }, [filteredCategories, currentCategoriesPage, totalCategoriesPages]);

  const filteredItems = useMemo(() => {
    if (!selectedCategory) return [];

    let exclusions: any[] = [];

    if (formData.name === selectedCategory && currentPattern) {
      const matchingPattern = formData.devicePatterns.find(
        (dp: DevicePattern) => dp.pattern === currentPattern,
      );
      exclusions = matchingPattern?.excludedDevices || [];
    } else {
      const selectedCat = categoryList.find((c) => c.name === selectedCategory);
      exclusions = selectedCat?.manualExclusions || [];
    }

    const excludedIds = new Set(
      exclusions
        .filter((exclusion: any) => exclusion.id != null)
        .map((exclusion: any) => exclusion.id),
    );

    const visibleItems = allSelectedResources.filter((item: any) => {
      let matchesSearch = false;
      if (selectedResourceType === "app") {
        matchesSearch = item.path
          ?.toLowerCase()
          .includes(itemsSearch.toLowerCase());
      } else if (selectedResourceType === "folder") {
        matchesSearch = item.path
          ?.toLowerCase()
          .includes(itemsSearch.toLowerCase());
      } else if (selectedResourceType === "url") {
        matchesSearch = item.name
          ?.toLowerCase()
          .includes(itemsSearch.toLowerCase());
      }

      if (!matchesSearch) return false;

      let isExcluded = false;
      if (selectedResourceType === "url") {
        isExcluded = item.id != null && excludedIds.has(item.id);
      } else {
        const excludedPaths = new Set(
          exclusions.map((exclusion: any) =>
            (exclusion.name || exclusion.path || "").toLowerCase().trim(),
          ),
        );
        const itemPath = (item.path || item.name || "").toLowerCase().trim();
        isExcluded = itemPath && excludedPaths.has(itemPath);
      }

      if (isExcluded) {
        return false;
      }

      return true;
    });

    const seenItems = new Set<string>();
    return visibleItems.filter((item: any) => {
      const normalizedPath = String(item?.path || "")
        .toLowerCase()
        .trim();
      const normalizedName = String(item?.name || "")
        .toLowerCase()
        .trim();
      const normalizedUrl = String(item?.url || "")
        .toLowerCase()
        .trim();
      const normalizedId =
        item?.id != null ? String(item.id).toLowerCase().trim() : "";

      let uniqueKey = "";
      if (selectedResourceType === "app" || selectedResourceType === "folder") {
        uniqueKey = normalizedPath || normalizedName;
      } else {
        uniqueKey = normalizedId || normalizedName || normalizedUrl;
      }

      if (!uniqueKey || seenItems.has(uniqueKey)) {
        return false;
      }

      seenItems.add(uniqueKey);
      return true;
    });
  }, [
    selectedCategory,
    itemsSearch,
    allSelectedResources,
    selectedResourceType,
    categoryList,
    formData,
    currentPattern,
  ]);

  const totalItemsPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);
  const paginatedItems = useMemo(() => {
    const startIndex = (currentItemsPage - 1) * ITEMS_PER_PAGE;
    return filteredItems.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredItems, currentItemsPage]);

  const deleteResource = async (type: string, category: string) => {
    await removeCategory(type, category, activeTab, tenantName);
    fetchCategoriesData(false);
  };

  const removeDuplicatesApps = (apps: any[] | undefined) => {
    if (!Array.isArray(apps)) return { uniqueApps: [], deletedApps: [] };
    const uniqueAppsMap = new Map<string, any>();
    const deletedApps: any[] = [];
    apps.forEach((current: any) => {
      const currentPath = current.path || "";
      const currentCategoryNames = current.categories
        ? current.categories
            .map((cat: any) => cat.categoryName)
            .sort()
            .join("|")
        : "";
      const uniqueKey = `${currentPath}|${currentCategoryNames}`;
      if (!uniqueAppsMap.has(uniqueKey)) uniqueAppsMap.set(uniqueKey, current);
      else deletedApps.push(current);
    });
    const uniqueApps = Array.from(uniqueAppsMap.values());
    uniqueApps.sort((a: any, b: any) => a.id - b.id);
    deletedApps.sort((a: any, b: any) => a.id - b.id);
    return { uniqueApps, deletedApps };
  };

  const fetchCategoryItems = async (category: any | null) => {
    if (!category) return;
    setItemsLoading(true);
    try {
      let response: any;
      let resources: any[] = [];
      const matchedId = null;

      if (matchedId) {
        const profileId = matchedId;
        response = await getCategoryItems(
          activeTab,
          tenantName,
          selectedResourceType,
          category.name,
          profileId,
        );
        if (selectedResourceType === "app") {
          const seen = new Set<string>();
          let filteredResponse = response.filter((entry: any) => {
            const appPath = entry?.path;
            if (!appPath || seen.has(appPath)) return false;
            seen.add(appPath);
            return true;
          });
          resources = filteredResponse;
        } else {
          resources = Array.isArray(response?.content)
            ? [...response.content].sort((a, b) => a.id - b.id)
            : [];
        }
        setAllSelectedResources(resources);
      } else {
        response = await getCategoryItemsWithoutProfileId(
          activeTab,
          tenantName,
          selectedResourceType,
          category.name,
        );
        if (selectedResourceType === "app") {
          const { uniqueApps, deletedApps } = removeDuplicatesApps(response);
          setRemovedAppDupicates(deletedApps);
          resources = uniqueApps;
        } else {
          resources = Array.isArray(response?.content)
            ? [...response.content].sort((a, b) => a.id - b.id)
            : [];
        }
        const filteredResources = resources.filter((r: any) =>
          r.categories?.some(
            (cat: any) =>
              cat.categoryName?.trim().toLowerCase() ===
              category.name?.trim().toLowerCase(),
          ),
        );

        setAllSelectedResources(filteredResources);
      }
    } catch (error) {
      console.error(error);
      setAllSelectedResources([]);
    } finally {
      setItemsLoading(false);
    }
  };

  const getResourceTypeIcon = (type: string) =>
    type.toLowerCase() === "app" ? (
      <FileTerminal className="w-4 h-4" />
    ) : type.toLowerCase() === "folder" ? (
      <Folder className="w-4 h-4" />
    ) : (
      <Globe className="w-4 h-4" />
    );
  const getResourceTypeName = (type: string) =>
    type.toLowerCase() === "app"
      ? "Applications"
      : type.toLowerCase() === "folder"
        ? "Folders"
        : "URLs";
  const getOSName = (os: string) =>
    os.toLowerCase() === "windows"
      ? "Windows"
      : os.toLowerCase() === "linux"
        ? "Linux"
        : "Mac";
  const getDynamicDialogTitle = () =>
    `Create Resource Category for ${getOSName(activeTab)} ${getResourceTypeName(selectedResourceType)}`;

  const addPattern = () => {
    if (formData.devicePatterns.some((dp) => dp.pattern === currentPattern))
      return;

    const newPattern: DevicePattern = {
      id: Date.now().toString() + Math.random().toString(),
      pattern: currentPattern,
      includedDevices: livePattern.filter((d) => d.included),
      excludedDevices: livePattern.filter((d) => !d.included),
    };
    setFormData({
      ...formData,
      devicePatterns: [...formData.devicePatterns, newPattern],
      searchPatterns: "",
    });

    setLivePattern([]);
    setCurrentPattern("");
  };

  const handleIncludeAll = () => {
    const updatedLivePattern = livePattern.map((resource) => ({
      ...resource,
      included: true,
    }));
    setLivePattern(updatedLivePattern);

    const updatedDevicePatterns = formData.devicePatterns.map(
      (dp: DevicePattern) => {
        if (dp.pattern === selectedPattern) {
          const updatedIncluded = [
            ...(dp.includedDevices || []),
            ...(dp.excludedDevices || []),
          ].map((res) => ({ ...res, included: true }));

          return {
            ...dp,
            includedDevices: updatedIncluded,
            excludedDevices: [],
          };
        }
        return dp;
      },
    );

    setFormData((prev) => ({
      ...prev,
      devicePatterns: updatedDevicePatterns,
    }));
  };

  const handleRemoveAll = () => {
    const updatedLivePattern = livePattern.map((resource) => ({
      ...resource,
      included: false,
    }));
    setLivePattern(updatedLivePattern);

    const updatedDevicePatterns = formData.devicePatterns.map(
      (dp: DevicePattern) => {
        if (dp.pattern === selectedPattern) {
          const updatedExcluded = [
            ...(dp.includedDevices || []),
            ...(dp.excludedDevices || []),
          ].map((res) => ({ ...res, included: false }));

          return {
            ...dp,
            includedDevices: [],
            excludedDevices: updatedExcluded,
          };
        }
        return dp;
      },
    );

    setFormData((prev) => ({
      ...prev,
      devicePatterns: updatedDevicePatterns,
    }));
  };

  const removePattern = (pattern: string) => {
    setFormData({
      ...formData,
      devicePatterns: formData.devicePatterns.filter(
        (dp) => dp.pattern !== pattern,
      ),
    });

    setPatterns(patterns.filter((p) => p !== pattern));
  };

  const getFirstNonEmpty = (...values: any[]) => {
    for (const value of values) {
      const normalized = String(value ?? "").trim();
      if (normalized) return normalized;
    }
    return "";
  };

  const getResourceName = (device: any) => {
    return selectedResourceType === "url"
      ? getFirstNonEmpty(
          device?.name,
          device?.urlName,
          device?.url,
          device?.defaultProfileUrlResource?.urlName,
          device?.defaultProfileUrlResource?.name,
          device?.computerUsersUrlControlMembership?.urlName,
          device?.computerUsersUrlControlMembership?.name,
        )
      : getFirstNonEmpty(
          device?.path,
          device?.appPath,
          device?.folderPath,
          device?.resourcePath,
          device?.name,
          device?.defaultProfileAppsResource?.appPath,
          device?.defaultProfileAppsResource?.path,
          device?.computerUsersPublishersAppsMembership?.path,
          device?.defaultProfileFoldersResource?.path,
          device?.computerUsersFoldersMembership?.path,
        );
  };

  const getResourceKey = (device: any) => {
    const idKey =
      device?.id != null ? String(device.id).trim().toLowerCase() : "";
    const nameKey = getResourceName(device).trim().toLowerCase();
    return idKey || nameKey;
  };

  const dedupeManualExclusions = (
    exclusions: Array<{ id: any; name: string }>,
  ) => {
    const dedupedMap = new Map<string, { id: any; name: string }>();

    exclusions.forEach((exclusion) => {
      const key =
        String(exclusion?.id ?? "")
          .trim()
          .toLowerCase() ||
        String(exclusion?.name ?? "")
          .trim()
          .toLowerCase();
      if (!key) return;
      if (!dedupedMap.has(key)) {
        dedupedMap.set(key, {
          id: exclusion?.id,
          name: String(exclusion?.name ?? "").trim(),
        });
      }
    });

    return Array.from(dedupedMap.values()).filter(
      (exclusion) => exclusion.id != null || exclusion.name,
    );
  };

  const buildManualExclusionsForEdit = () => {
    const exclusions = formData.devicePatterns.flatMap((dp: DevicePattern) => {
      const selectedResources = dp.includedDevices || [];
      const selectedKeys = new Set(
        selectedResources.map(getResourceKey).filter(Boolean),
      );

      const currentPatternResources =
        selectedPattern === dp.pattern &&
        Array.isArray(livePattern) &&
        livePattern.length > 0
          ? livePattern
          : [...(dp.includedDevices || []), ...(dp.excludedDevices || [])];

      return currentPatternResources
        .filter((device: any) => {
          const key = getResourceKey(device);
          if (!key) return false;
          return !selectedKeys.has(key);
        })
        .map((device: any) => ({
          id: device?.id,
          name: getResourceName(device),
        }));
    });

    return dedupeManualExclusions(exclusions);
  };

  const buildManualExclusionsForCreate = () => {
    const exclusions = formData.devicePatterns
      .flatMap((dp: DevicePattern) => [...(dp.excludedDevices || [])])
      .map((device: any) => ({
        id: device?.id,
        name: getResourceName(device),
      }));

    return dedupeManualExclusions(exclusions);
  };

  const handleSubmitCategory = async () => {
    if (
      !categoryName.trim() ||
      formData.devicePatterns.length === 0 ||
      !hasAtLeastOneSelectedResource
    )
      return;

    if (formData.isEdit) {
      const searchPatterns = formData.devicePatterns.map((dp) => ({
        id: dp.id || Date.now().toString() + Math.random().toString(),
        text: dp.pattern,
      }));

      const manualExclusions = buildManualExclusionsForEdit();
      let osType =
        activeTab.toUpperCase() === "MAC" || activeTab.toUpperCase() === "MACOS"
          ? "MACOS"
          : activeTab.toUpperCase();
      const payload = {
        newName: categoryName.trim(),
        reputationScore: priority || 10,
        newSearchPatterns: searchPatterns,
        oldName: formData.name,
        oldSearchPatterns: formData.devicePatterns.map((dp) => dp.pattern),
        os: osType,
        resourceType: selectedResourceType.toUpperCase(),
        userEmail: email,
        manualExclusions,
        tenantName: tenantName,
      };
      await updateCategory(payload);
    } else {
      const searchPatterns = formData.devicePatterns.map((dp) => ({
        id: dp.id || Date.now().toString() + Math.random().toString(),
        text: dp.pattern,
      }));

      const manualExclusions = buildManualExclusionsForCreate();

      let osType =
        activeTab.toUpperCase() === "MAC" || activeTab.toUpperCase() === "MACOS"
          ? "MACOS"
          : activeTab.toUpperCase();

      const payload = {
        name: categoryName.trim(),
        reputationScore: priority || 10,
        searchPatterns,
        os: osType,
        resourceType: selectedResourceType.toUpperCase(),
        profiles: formData.profiles || [],
        userEmail: email,
        manualExclusions,
        tenantName: tenantName,
      };
      await postCategory(payload);
    }

    setIsCreateDialogOpen(false);
    setCategoryName("");
    setCurrentPattern("");
    setFormData(getDefaultFormData());
    resetStateOnTabOrTypeChange();
    fetchCategoriesData(false);
  };

  const displayedLivePattern = useMemo(() => {
    if (!selectedPattern) {
      return [];
    }

    if (livePattern.length > 0) {
      return livePattern;
    }

    const devicePattern = formData.devicePatterns.find(
      (dp) => dp.pattern === selectedPattern,
    );

    if (!devicePattern) {
      return [];
    }

    const combined = [
      ...(devicePattern.includedDevices || []),
      ...(devicePattern.excludedDevices || []),
    ];

    return combined;
  }, [livePattern, selectedPattern, formData.devicePatterns]);

  const hasAtLeastOneSelectedResource = useMemo(() => {
    return formData.devicePatterns.some(
      (dp: DevicePattern) => (dp.includedDevices?.length || 0) > 0,
    );
  }, [formData.devicePatterns]);

  const getPatternResources = useCallback(
    (pattern: string) => {
      if (!pattern.trim()) {
        setLivePattern([]);
        return;
      }

      setLivePatternLoading(true);
      getResourcesByPattern(
        activeTab,
        tenantName,
        selectedResourceType,
        pattern,
      )
        .then((response: any) => {
          let resources: any[] = [];

          if (Array.isArray(response)) {
            resources = response;
          } else if (selectedResourceType === "app") {
            const { uniqueApps, deletedApps } = removeDuplicatesApps(response);
            setRemovedAppDupicates(deletedApps);
            resources = uniqueApps;
          } else {
            resources = Array.isArray(response?.content)
              ? [...response.content].sort((a, b) => a.id - b.id)
              : [];
          }

          const seen = new Set<string>();
          const deduplicatedResources = resources.filter((res: any) => {
            const id = res.id ? String(res.id).toLowerCase().trim() : "";
            const path = res.path ? String(res.path).toLowerCase().trim() : "";
            const url = res.url ? String(res.url).toLowerCase().trim() : "";
            const name = res.name ? String(res.name).toLowerCase().trim() : "";

            let key = "";
            if (path) {
              key = path;
            } else if (url) {
              key = url;
            } else if (id) {
              key = id;
            } else if (name) {
              key = name;
            } else {
              return false;
            }

            if (seen.has(key)) {
              return false;
            }

            seen.add(key);
            return true;
          });

          const devicePattern = formData.devicePatterns.find(
            (dp: DevicePattern) => dp.pattern === pattern,
          );
          const isNewPattern = !devicePattern;
          const hasNoSavedSelection =
            !!devicePattern &&
            (devicePattern.includedDevices?.length || 0) === 0 &&
            (devicePattern.excludedDevices?.length || 0) === 0;
          const shouldAutoSelectAll =
            !formData.isEdit || isNewPattern || hasNoSavedSelection;

          const withIncluded = deduplicatedResources.map((res: any) => {
            if (shouldAutoSelectAll) {
              return {
                ...res,
                included: true,
              };
            }

            const isInIncluded = (devicePattern?.includedDevices || []).some(
              (included: any) => {
                if (
                  included.id != null &&
                  res.id != null &&
                  included.id === res.id
                )
                  return true;
                if (selectedResourceType === "url") {
                  return (included.name || "") === (res.name || "");
                }
                return (included.path || "") === (res.path || "");
              },
            );

            const isInExcluded = (devicePattern?.excludedDevices || []).some(
              (excluded: any) => {
                if (
                  excluded.id != null &&
                  res.id != null &&
                  excluded.id === res.id
                )
                  return true;
                if (selectedResourceType === "url") {
                  return (excluded.name || "") === (res.name || "");
                }

                const excludedPath = (excluded.name || excluded.path || "")
                  .toLowerCase()
                  .trim();
                const resPath = (res.path || "").toLowerCase().trim();
                return !!excludedPath && excludedPath === resPath;
              },
            );

            return {
              ...res,
              included: isInIncluded || !isInExcluded,
            };
          });

          setLivePattern(withIncluded);

          if (!isNewPattern) {
            const includedDevices = withIncluded.filter(
              (resource: any) => resource.included,
            );
            const excludedDevices = withIncluded.filter(
              (resource: any) => !resource.included,
            );

            setFormData((prev) => ({
              ...prev,
              devicePatterns: prev.devicePatterns.map((dp: DevicePattern) =>
                dp.pattern === pattern
                  ? {
                      ...dp,
                      includedDevices,
                      excludedDevices,
                    }
                  : dp,
              ),
            }));
          }

          console.log(
            "Loaded resources:",
            withIncluded.length,
            "Included:",
            withIncluded.filter((r: any) => r.included).length,
          );
        })
        .catch((err) => {
          console.error("Error fetching pattern resources:", err);
          setLivePattern([]);
        })
        .finally(() => {
          setLivePatternLoading(false);
        });
    },
    [
      activeTab,
      tenantName,
      selectedResourceType,
      removeDuplicatesApps,
      formData.devicePatterns,
    ],
  );

  useEffect(() => {
    if (!currentPattern.trim()) {
      setLivePattern([]);
      return;
    }

    if (isSelectionRef.current) {
      isSelectionRef.current = false;
      getPatternResources(currentPattern);
    }
  }, [currentPattern]);

  const handleSearchPattern = () => {
    const pattern = currentPattern.trim();

    if (!pattern) {
      setLivePattern([]);
      setSelectedPattern("");
      return;
    }

    setSelectedPattern(pattern);
    setCurrentPattern(pattern);
    getPatternResources(pattern);
  };

  const handleEditCategory = useCallback((category: AppResource) => {
    const existingPatterns: DevicePattern[] = category.searchPatterns.map(
      (pattern) => {
        const patternExclusions = category.manualExclusions || [];

        return {
          id: Date.now().toString() + Math.random().toString(),
          pattern: pattern,
          includedDevices: [],
          excludedDevices: patternExclusions,
        };
      },
    );

    setCategoryName(category.name);
    setPriority(Number(category.reputationScore));

    setSelectedPattern("");
    setCurrentPattern("");
    setLivePattern([]);

    setFormData({
      name: category.name,
      reputationScore: category.reputationScore,
      profiles: [],
      devicePatterns: existingPatterns,
      searchPatterns: category.searchPatterns.join(", ") || "",
      tagInput: "",
      isEdit: true,
    });

    setIsCreateDialogOpen(true);
  }, []);

  return (
    <div className="resource-categories">
      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          setActiveTab(value as any);
          resetStateOnTabOrTypeChange();
        }}
        className="category-layout"
      >
        <div className="category-toolbar">
          <TabsList unstyled className="category-os-tabs">
            <TabsTrigger
              unstyled
              value="windows"
              className="category-os-tab"
              data-testid="tab-windows"
            >
              <FaWindows />
              Windows
            </TabsTrigger>
            <TabsTrigger
              unstyled
              value="mac"
              className="category-os-tab"
              data-testid="tab-mac"
            >
              <FaApple />
              Mac
            </TabsTrigger>
            <TabsTrigger
              unstyled
              value="linux"
              className="category-os-tab"
              data-testid="tab-linux"
            >
              <FaLinux />
              Linux
            </TabsTrigger>
          </TabsList>
          <div className="category-toolbar-actions">
            <Button onClick={exportData} size="sm" variant="outline">
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
            <Button onClick={importData} size="sm" variant="outline">
              <Upload className="w-4 h-4 mr-2" /> Import
            </Button>
            <input
              type="file"
              ref={inputRef}
              accept=".json"
              onChange={handleFileChange}
              className="hidden"
            />
            <Button
              onClick={() => {
                handleResetCategory();
                setIsCreateDialogOpen(true);
              }}
              size="sm"
              className="category-primary-action"
              data-testid="button-create-category"
            >
              <Plus className="w-4 h-4 mr-2" /> Create new category
            </Button>
          </div>
        </div>

        <TabsContent value={activeTab} className="category-content">
          <div className="category-workspace">
            <div className="category-types">
              <Card className="category-panel">
                <CardHeader className="category-panel-header">
                  <CardTitle className="category-panel-title">
                    <span className="category-icon">
                      <Layers />
                    </span>
                    Types
                  </CardTitle>
                </CardHeader>
                <CardContent className="category-types-content">
                  <div className="space-y-1">
                    {["app", "folder", "url"].map((type) => (
                      <Button
                        key={type}
                        variant={
                          selectedResourceType === type ? "default" : "ghost"
                        }
                        className="category-type"
                        aria-current={
                          selectedResourceType === type ? "true" : undefined
                        }
                        onClick={() => {
                          setSelectedResourceType(type as any);
                          resetStateOnTabOrTypeChange();
                        }}
                      >
                        {type === "app" ? (
                          <FileTerminal className="w-3 h-3" />
                        ) : type === "folder" ? (
                          <Folder className="w-3 h-3" />
                        ) : (
                          <Globe className="w-3 h-3" />
                        )}
                        {type === "app"
                          ? "Apps"
                          : type === "folder"
                            ? "Folders"
                            : "URLs"}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="category-column">
              <Card className="category-panel category-list-panel">
                <CardHeader className="category-panel-header">
                  <div className="category-panel-heading">
                    <CardTitle className="category-panel-title">
                      <span className="category-icon">
                        {getResourceTypeIcon(selectedResourceType)}
                      </span>
                      {getResourceTypeName(selectedResourceType)}{" "}
                      <span className="category-count">
                        {filteredCategories.length}
                      </span>
                    </CardTitle>
                    <div className="category-search">
                      <Search aria-hidden="true" />
                      <Input
                        aria-label="Search categories"
                        placeholder="Search categories..."
                        value={categoriesSearch}
                        onChange={(e) => setCategoriesSearch(e.target.value)}
                        className="h-8"
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="category-panel-content">
                  {categoriesLoading ? (
                    <div className="category-scroll category-rows">
                      {Array.from({ length: ITEMS_PER_PAGE }).map(
                        (_, index) => (
                          <div key={index} className="category-skeleton-row">
                            <Skeleton className="category-skeleton-icon" />
                            <div className="category-skeleton-copy">
                              <Skeleton className="h-3 w-3/4" />
                              <Skeleton className="h-2 w-1/2" />
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  ) : paginatedCategories.length > 0 ? (
                    <div className="category-scroll">
                      <div className="grid gap-2">
                        {paginatedCategories.map((category, index) => (
                          <Card
                            key={index}
                            className="category-entry"
                            aria-current={
                              selectedCategory === category.name
                                ? "true"
                                : undefined
                            }
                            onClick={() => {
                              fetchCategoryItems(category);
                              setSelectedCategory(category.name);
                            }}
                          >
                            <CardContent className="category-entry-content">
                              <div className="category-entry-row">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <div className="category-priority">
                                      {category.reputationScore}
                                    </div>
                                    {getResourceTypeIcon(category.resourceType)}
                                    <h3 className="font-medium text-sm break-words">
                                      {category.name}
                                    </h3>
                                  </div>
                                  <div className="category-date">
                                    <Clock aria-hidden="true" />
                                    {formatDate(category.createdAt)}
                                  </div>
                                </div>

                                <div className="category-entry-actions">
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="category-icon-button"
                                    aria-label={`Edit ${category.name}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      console.log(category);
                                      handleEditCategory(category);
                                    }}
                                    data-testid={`button-edit-category-${category.name}`}
                                  >
                                    <Edit3 className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="category-icon-button text-destructive"
                                    aria-label={`Delete ${category.name}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedCategory(category.name);
                                      setShowDeleteConfirmation(true);
                                    }}
                                    data-testid={`button-delete-category-${category.name}`}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="category-empty">
                      <div className="category-empty-icon">
                        {getResourceTypeIcon(selectedResourceType)}
                      </div>
                      <h3 className="text-lg font-medium mb-2">
                        No categories found
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        {categoriesSearch
                          ? "No categories match your search."
                          : `No ${selectedResourceType} categories found for ${activeTab}.`}
                      </p>
                      <Button
                        className="category-primary-action"
                        onClick={() => {
                          handleResetCategory();
                          setIsCreateDialogOpen(true);
                        }}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Create first category
                      </Button>
                    </div>
                  )}
                  {totalCategoriesPages > 1 && (
                    <div className="category-pagination">
                      <p className="text-sm text-muted-foreground">
                        Showing{" "}
                        {Math.min(
                          (currentCategoriesPage - 1) * CATEGORIES_PER_PAGE + 1,
                          filteredCategories.length,
                        )}{" "}
                        to{" "}
                        {Math.min(
                          currentCategoriesPage * CATEGORIES_PER_PAGE,
                          filteredCategories.length,
                        )}{" "}
                        of {filteredCategories.length} categories
                      </p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            setCurrentCategoriesPage((prev) =>
                              Math.max(1, prev - 1),
                            )
                          }
                          disabled={currentCategoriesPage === 1}
                          data-testid="button-previous-categories-page"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            setCurrentCategoriesPage((prev) =>
                              Math.min(totalCategoriesPages, prev + 1),
                            )
                          }
                          disabled={
                            currentCategoriesPage === totalCategoriesPages
                          }
                          data-testid="button-next-categories-page"
                        >
                          Next
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="category-column">
              <Card className="category-panel category-list-panel">
                <CardHeader className="category-panel-header">
                  <div className="category-panel-heading">
                    <CardTitle className="category-panel-title">
                      <span className="category-icon">
                        {getResourceTypeIcon(selectedResourceType)}
                      </span>
                      Items{" "}
                      <span className="category-count">
                        {filteredItems.length}
                      </span>
                    </CardTitle>
                    <div className="category-search">
                      <Search aria-hidden="true" />
                      <Input
                        aria-label="Search items"
                        placeholder="Search items..."
                        value={itemsSearch}
                        onChange={(e) => setItemsSearch(e.target.value)}
                        className="h-8"
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="category-panel-content">
                  {itemsLoading ? (
                    <div className="category-scroll category-rows">
                      {Array.from({ length: ITEMS_PER_PAGE }).map(
                        (_, index) => (
                          <div key={index} className="category-skeleton-row">
                            <Skeleton className="category-skeleton-icon" />
                            <div className="category-skeleton-copy">
                              <Skeleton className="h-3 w-3/4" />
                              <Skeleton className="h-2 w-1/2" />
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  ) : selectedCategory ? (
                    paginatedItems.length > 0 ? (
                      <div className="category-scroll category-rows">
                        {paginatedItems.map((item) => {
                          let displayValue = "";
                          switch (selectedResourceType) {
                            case "app":
                              displayValue = item?.path ?? "";
                              break;
                            case "folder":
                              displayValue = item.path ?? "";
                              break;
                            case "url":
                              displayValue = item.name ?? "";
                              break;
                          }
                          return (
                            <Card key={item.id} className="category-item">
                              <CardContent className="category-item-content">
                                <div className="flex items-center gap-3">
                                  <div className="category-icon">
                                    {getResourceTypeIcon(selectedResourceType)}
                                  </div>
                                  <div className="text-sm font-mono text-foreground break-all">
                                    {displayValue || "N/A"}
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="category-empty">
                        <span className="category-empty-icon">
                          {getResourceTypeIcon(selectedResourceType)}
                        </span>
                        <h3>No items found</h3>
                        <p>
                          {itemsSearch
                            ? "No resources match your search."
                            : "This category has no matching resources."}
                        </p>
                      </div>
                    )
                  ) : (
                    <div className="category-empty">
                      <span className="category-empty-icon">
                        <List />
                      </span>
                      <h3>No category selected</h3>
                      <p>Select a category to view its resources.</p>
                    </div>
                  )}

                  {selectedCategory && totalItemsPages > 1 && (
                    <div className="category-pagination">
                      <div className="text-sm text-muted-foreground">
                        Page {currentItemsPage} of {totalItemsPages}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setCurrentItemsPage((prev) => Math.max(1, prev - 1))
                          }
                          disabled={currentItemsPage === 1}
                          data-testid="button-items-prev"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setCurrentItemsPage((prev) =>
                              Math.min(totalItemsPages, prev + 1),
                            )
                          }
                          disabled={currentItemsPage === totalItemsPages}
                          data-testid="button-items-next"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(open) => setIsCreateDialogOpen(open)}
      >
        <DialogContent className="management-dialog category-dialog">
          <DialogHeader className="management-dialog-header">
            <div className="management-dialog-heading">
              <span className="management-dialog-icon">
                {formData.isEdit ? <Edit3 /> : <Plus />}
              </span>
              <div>
                <DialogTitle>
                  {formData.isEdit
                    ? "Edit Resource Category"
                    : getDynamicDialogTitle()}
                </DialogTitle>
                <DialogDescription>
                  Organize {getOSName(activeTab)}{" "}
                  {getResourceTypeName(selectedResourceType).toLowerCase()}{" "}
                  using resource patterns.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="management-dialog-body">
            <div className="category-form-fields">
              <div className="management-form-field">
                <Label htmlFor="category-name" className="text-sm font-medium">
                  Category Name *
                </Label>
                <Input
                  id="category-name"
                  placeholder="Enter category name"
                  value={categoryName}
                  maxLength={DESCRIPTION_MAX_LENGTH}
                  onChange={(e) =>
                    setCategoryName(
                      e.target.value.slice(0, DESCRIPTION_MAX_LENGTH),
                    )
                  }
                  data-testid="input-category-name"
                  className="mt-1"
                />
                <p className="text-[10px] text-muted-foreground text-right">
                  {categoryName.length}/{DESCRIPTION_MAX_LENGTH}
                </p>
              </div>
              <div className="management-form-field">
                <Label htmlFor="priority" className="text-sm font-medium">
                  Priority *
                </Label>
                <Input
                  id="priority"
                  type="number"
                  defaultValue={priority}
                  placeholder="Enter priority"
                  data-testid="input-priority"
                  className="mt-1"
                  onChange={(e) => setPriority(Number(e.target.value) || 0)}
                />
              </div>
            </div>

            <div className="management-form-section">
              <Label className="management-section-title">
                <Search />
                Resource patterns
              </Label>
              <div className="flex flex-wrap gap-2 mb-3">
                {formData.devicePatterns.map((pattern, index) => (
                  <Badge
                    key={index}
                    variant={
                      selectedPattern === pattern.pattern
                        ? "default"
                        : "secondary"
                    }
                    className="category-pattern"
                    data-selected={selectedPattern === pattern.pattern}
                    onClick={() => {
                      isSelectionRef.current = true;
                      console.log("Selected pattern:", pattern);
                      setSelectedPattern(pattern.pattern);
                      setCurrentPattern(pattern.pattern);
                    }}
                  >
                    {pattern.pattern}
                    <button
                      type="button"
                      className="category-pattern-remove"
                      aria-label={`Remove pattern ${pattern.pattern}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPattern("");
                        setCurrentPattern("");
                        removePattern(pattern.pattern);
                      }}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>

              <div className="category-pattern-input">
                <div className="management-form-field">
                  <Label
                    htmlFor="pattern-input"
                    className="text-xs text-primary"
                  >
                    Pattern
                  </Label>
                  <div className="flex gap-1 items-center">
                    <Input
                      id="pattern-input"
                      placeholder="Enter pattern (e.g., nslookup, *.exe)"
                      data-testid="input-pattern"
                      className="border-2 border-primary/20 focus:border-primary/40"
                      value={currentPattern}
                      onChange={(e) => setCurrentPattern(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleSearchPattern()
                      }
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="category-pattern-search"
                      aria-label="Search matching resources"
                      data-testid="button-search-pattern"
                      onClick={handleSearchPattern}
                      disabled={!currentPattern.trim() || livePatternLoading}
                      title="Search for resources matching this pattern"
                    >
                      {livePatternLoading ? (
                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Search className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="text-primary border-primary/20"
                  data-testid="button-add-pattern"
                  onClick={addPattern}
                  disabled={!currentPattern.trim()}
                >
                  Add pattern
                </Button>
              </div>
            </div>

            <div className="management-form-section category-matches-section">
              <div className="category-matches-toolbar">
                <Label className="management-section-title">
                  <Layers />
                  Matched resources
                </Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="button-include-all"
                    onClick={handleIncludeAll}
                    disabled={livePatternLoading || !selectedPattern}
                  >
                    Include all
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="button-remove-all"
                    onClick={handleRemoveAll}
                    disabled={livePatternLoading || !selectedPattern}
                  >
                    Remove all
                  </Button>
                </div>
              </div>

              <div className="category-matches">
                <div className="category-matches-header grid grid-cols-12 gap-4">
                  <div className="col-span-1">Sl No</div>
                  <div className="col-span-9">Resource</div>
                  <div className="col-span-2">Included</div>
                </div>
                <div className="category-matches-list">
                  {!selectedPattern ? (
                    <div className="p-3 text-center text-sm text-muted-foreground">
                      Select a pattern above to view matching resources
                    </div>
                  ) : livePatternLoading ? (
                    <div className="space-y-2 p-3">
                      {Array.from({ length: 5 }).map((_, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-12 gap-4 p-2"
                        >
                          <Skeleton className="col-span-1 h-4" />
                          <Skeleton className="col-span-9 h-4" />
                          <Skeleton className="col-span-2 h-4" />
                        </div>
                      ))}
                    </div>
                  ) : displayedLivePattern.length === 0 ? (
                    <div className="p-3 text-center text-sm text-muted-foreground">
                      No resources match this pattern
                    </div>
                  ) : (
                    displayedLivePattern.map((resource, index) => (
                      <div
                        key={resource.id}
                        className="grid grid-cols-12 gap-4 p-3 border-b text-sm last:border-b-0"
                      >
                        <div className="col-span-1">{index + 1}</div>
                        <div className="col-span-9 font-mono text-xs break-all">
                          {resource.path || resource.name}
                        </div>
                        <div className="col-span-2">
                          <input
                            type="checkbox"
                            checked={resource.included}
                            className="category-resource-checkbox"
                            aria-label={`Include ${resource.path || resource.name}`}
                            data-testid={`checkbox-resource-${resource.id}`}
                            onChange={(e) => {
                              const checked = e.target.checked;

                              setLivePattern((prev) =>
                                prev.map((r) =>
                                  r.id === resource.id
                                    ? { ...r, included: checked }
                                    : r,
                                ),
                              );
                              console.log(selectedPattern);
                              const updatedDevicePatterns =
                                formData.devicePatterns.map(
                                  (dp: DevicePattern) => {
                                    if (dp.pattern === selectedPattern) {
                                      const newIncluded =
                                        dp.includedDevices.filter(
                                          (d) => d.id !== resource.id,
                                        );
                                      const newExcluded =
                                        dp.excludedDevices.filter(
                                          (d) => d.id !== resource.id,
                                        );

                                      if (checked) {
                                        newIncluded.push({
                                          ...resource,
                                          included: true,
                                        });
                                      } else {
                                        newExcluded.push({
                                          ...resource,
                                          included: false,
                                        });
                                      }

                                      return {
                                        ...dp,
                                        includedDevices: newIncluded,
                                        excludedDevices: newExcluded,
                                      };
                                    }
                                    return dp;
                                  },
                                );

                              setFormData((prev) => ({
                                ...prev,
                                devicePatterns: updatedDevicePatterns,
                              }));
                            }}
                          />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="management-dialog-footer">
            <Button
              variant="outline"
              onClick={() => {
                resetStateOnTabOrTypeChange();
                setIsCreateDialogOpen(false);
              }}
              data-testid="button-cancel"
              className="px-6"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitCategory}
              disabled={
                !categoryName.trim() ||
                !priority ||
                formData.devicePatterns.length === 0 ||
                !hasAtLeastOneSelectedResource ||
                livePatternLoading
              }
              data-testid="button-submit"
              className="category-primary-action"
            >
              {formData.isEdit ? "Save changes" : "Create category"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        className="management-dialog management-confirmation"
        open={showDeleteConfirmation}
        onOpenChange={setShowDeleteConfirmation}
        title="Delete Category"
        description={`Are you sure, you want to delete this category - ${selectedCategory}?`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={() => {
          deleteResource(selectedResourceType, selectedCategory);
        }}
      />
    </div>
  );
}
